import { css, useTheme } from '@emotion/react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot } from 'lexical';
import { useEffect, useState } from 'react';

import { $isSectionDividerNode } from './section-divider-node';

const STYLED_ATTR = 'data-section-styled';

function stickyBg(bgColor: string) {
  return `linear-gradient(180deg, ${bgColor} 60%, transparent)`;
}

function styleDivider(el: HTMLElement, bgColor: string) {
  if (el.getAttribute(STYLED_ATTR)) {
    // Just update background on theme change
    el.style.background = stickyBg(bgColor);
    return;
  }

  el.setAttribute(STYLED_ATTR, 'true');

  // Restructure: wrap existing children (hr + span + hr) in a row div
  const row = document.createElement('div');
  row.style.display = 'flex';
  row.style.alignItems = 'center';
  row.style.gap = '12px';

  while (el.firstChild) {
    row.appendChild(el.firstChild);
  }

  el.appendChild(row);

  // Restyle the outer div as a sticky column wrapper
  el.style.display = 'flex';
  el.style.flexDirection = 'column';
  el.style.alignItems = 'stretch';
  el.style.gap = '0';
  el.style.margin = '0';
  el.style.padding = '16px 0 0';
  el.style.paddingBottom = '24px';
  el.style.userSelect = 'none';
  el.style.position = 'sticky';
  el.style.top = '0';
  el.style.zIndex = '1';
  el.style.background = stickyBg(bgColor);
}

export function SectionTitlePlugin() {
  const [editor] = useLexicalComposerContext();
  const [hasDividers, setHasDividers] = useState(false);
  const theme = useTheme();
  const bgColor = theme.colors.background;

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      const dividerKeys: string[] = [];

      editorState.read(() => {
        const root = $getRoot();
        for (const child of root.getChildren()) {
          if ($isSectionDividerNode(child)) {
            dividerKeys.push(child.getKey());
          }
        }
      });

      setHasDividers(dividerKeys.length > 0);

      for (let i = 0; i < dividerKeys.length; i++) {
        const el = editor.getElementByKey(dividerKeys[i]);
        if (!el) continue;

        styleDivider(el, bgColor);

        const labelSpan = el.querySelector('[data-section-label]');
        if (labelSpan) {
          labelSpan.textContent = `Section ${i + 2}`;
        }
      }
    });
  }, [editor, bgColor]);

  if (!hasDividers) return null;

  return (
    <div
      data-testid="section-1-label"
      css={css`
        position: sticky;
        top: 0;
        z-index: 1;
        background: ${stickyBg(bgColor)};
        padding: 16px 0 24px;
        user-select: none;
        pointer-events: none;
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 12px;
        `}
      >
        <hr
          css={css`
            flex: 1;
            border: none;
            border-top: 1px solid currentColor;
            opacity: 0.15;
          `}
        />
        <span
          css={css`
            font-size: 0.7rem;
            font-weight: 500;
            letter-spacing: 0.02em;
            opacity: 0.35;
            white-space: nowrap;
          `}
        >
          Section 1
        </span>
        <hr
          css={css`
            flex: 1;
            border: none;
            border-top: 1px solid currentColor;
            opacity: 0.15;
          `}
        />
      </div>
    </div>
  );
}
